import { useContext } from 'react';
import * as R from 'ramda';

import { useAssetsWithElementsSearch } from '@atom/hooks/useAssetsWithElementsSearch';
import { List, Progress } from '@atom/mui';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { getCategoryPathNames } from '@atom/utilities/categoryUtilities';
import { highlightedText } from '@atom/utilities/textDisplayUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import LoadAssetsButton from './LoadAssetsButton';
import TreeContext from './TreeContext';
import TreeRowIcon from './TreeRowIcon';

import classes from './inventoryAssetTree.module.css';

const { ListItemButton, ListItemText } = List;

interface Props {
  includeInactive?: boolean;
  query: string;
}

const SEARCH_ASSETS_LIMIT = 12;

const InventoryAssetSearch = ({ includeInactive = true, query }: Props) => {
  const {
    handleAssetClick,
    rootTree: tree,
    filterParams,
    removeAssetId,
  } = useContext(TreeContext);

  const { elementSchemaId } = filterParams;

  const [
    { assets },
    { loadingSearch, loadingPagination, error, hasMore, fetchMore },
  ] = useAssetsWithElementsSearch({
    query,
    schemaId: elementSchemaId,
    includeMaterialAssets: true,
    includeInactive,
    includeLocationBasedAssets: true,
    limit: SEARCH_ASSETS_LIMIT,
    characterMin: 3,
    includeAttributesProperty: true,
    isVisibleAsSubtextAttributesOnly: true,
    includeElements: true,
  });

  const getAttributeSubText = (element: any): string => {
    if (isNilOrEmpty(element.attributes)) {
      return '';
    }

    const attributeIds = R.pathOr([], ['attributeGroups'], element).reduce(
      (acc, attributeGroup) => {
        return [...acc, ...R.pathOr([], ['attributes'], attributeGroup)];
      },
      [],
    );

    return attributeIds.reduce((acc: string, attributeId: string): any => {
      const attribute = element.attributes[attributeId];
      const label = attribute.name;
      const value = attributeDisplayUtility.display(
        attribute.value,
        attribute.dataType,
        attribute.unit,
      );

      const text = `${label}: ${value}`;
      return acc ? `${acc}, ${text}` : text;
    }, '');
  };

  return (
    <>
      {loadingSearch || !tree ? (
        <div className={classes.progressContainer}>
          <Progress />
        </div>
      ) : (
        <List>
          {assets.map(asset => {
            const rootAssetId = asset.isSubItem
              ? R.head(asset.ancestors).id
              : asset.id;

            if (rootAssetId === removeAssetId) {
              return <div />;
            }

            const path = asset?.isSubItem
              ? [
                  ...getCategoryPathNames(tree, asset.categoryPath),
                  ...asset.ancestors.map(ancestor => ancestor.name),
                ].join(' / ')
              : getCategoryPathNames(tree, asset.categoryPath).join(' / ');

            const subtext = asset?.isSubItem ? (
              <span className={classes.elementSubtext}>
                <span>{path}</span>
                <span>
                  {highlightedText(query, getAttributeSubText(asset), 600)}
                </span>
              </span>
            ) : (
              `${asset.assetType} \u2022 ${path}`
            );

            const name = highlightedText(query, asset.name, 600);

            return (
              <ListItemButton
                key={asset.id}
                onClick={() =>
                  handleAssetClick(asset, [
                    ...asset.ancestors,
                    { id: asset.id, name: asset.name },
                  ])
                }
                disabled={elementSchemaId && asset.schemaId !== elementSchemaId}
              >
                <TreeRowIcon tree={asset} />
                <ListItemText primary={name} secondary={subtext} />
              </ListItemButton>
            );
          })}
        </List>
      )}
      {hasMore && (
        <div className={classes.loadButtonContainer}>
          <LoadAssetsButton
            loading={loadingPagination}
            error={error}
            onClick={fetchMore}
          />
        </div>
      )}
    </>
  );
};

export default InventoryAssetSearch;
